import { LinearProgress } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  getApartmentByCodeSuccess,
  getApartmentMedia
} from '../../../actions/apartments'
import { UpdateStatus } from '../../../actions/types'
import {
  selectApartmentByCode,
  selectApartmentFilesByApartmentId
} from '../../../selectors/apartments'
import { IndexedApplications, StateInterface } from '../../../types/states'
import ApartmentsService from '../../../services/apartments'
import ApartmentInformation from './ApartmentInformation'

const PublicListing: FC = () => {
  const { apartmentCode } = useParams<{ apartmentCode: string }>()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const apartment = useSelector(selectApartmentByCode)
  const files = useSelector((state: StateInterface) =>
    apartment?.apartmentId
      ? selectApartmentFilesByApartmentId(apartment.apartmentId)(state) || []
      : []
  )
  const [isNotPublished, setIsNotPublished] = useState(false)

  const emptyApplications: IndexedApplications = {} as IndexedApplications
  const noopUpdateStatus: UpdateStatus = (_status) => undefined

  useEffect(() => {
    let mounted = true
    const loadListing = async () => {
      if (!apartmentCode) return
      try {
        const basicApartment = await ApartmentsService.getApartmentByCode(apartmentCode)
        if (!mounted) return
        dispatch(getApartmentByCodeSuccess(basicApartment))

        const isMarketing = basicApartment.status === 'marketing'
        setIsNotPublished(!isMarketing)

        if (!isMarketing) return

        const listingApartment = await ApartmentsService.getApartmentListingData(apartmentCode)
        if (!mounted) return
        dispatch(getApartmentByCodeSuccess(listingApartment))
        setIsNotPublished(false)
      } catch (_error) {
        if (mounted) {
          setIsNotPublished(true)
        }
      }
    }

    loadListing()
    return () => {
      mounted = false
    }
  }, [dispatch, apartmentCode])

  useEffect(() => {
    if (apartment?.apartmentId && apartment.code === apartmentCode) {
      dispatch(getApartmentMedia(apartment.apartmentId))
    }
  }, [dispatch, apartment?.apartmentId, apartment?.code, apartmentCode])

  useEffect(() => {
    if (apartment?.apartmentId && apartment.code === apartmentCode) {
      // Log the raw apartment payload to help debug missing fields on the public listing view.
      console.log('PublicListing apartment payload', apartment)
    }
  }, [apartment, apartmentCode])

  if (!apartment || apartment.code !== apartmentCode) {
    return <LinearProgress />
  }

  const notPublishedBanner = isNotPublished ? (
    <div
      style={{
        marginTop: 16,
        marginBottom: 12,
        marginLeft: 16,
        marginRight: 16,
        padding: '8px 12px',
        background: '#fff7e6',
        color: '#8a4b00',
        border: '1px solid #f0ad4e',
        borderRadius: 4
      }}
    >
      {t('apartment.overview.notPublished')}
    </div>
  ) : null

  return (
    <>
      {notPublishedBanner}
      <ApartmentInformation
        t={t}
        files={files}
        apartment={apartment}
        user={undefined}
        applications={emptyApplications}
        isAdmin={false}
        updateStatus={noopUpdateStatus}
        hideLandlordPanel
        containerStyle={{ marginTop: 18 }}
      />
    </>
  )
}

export default PublicListing
